import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #stateVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onLinkClick);
    // `toggle` doesn't bubble, so listen in the capture phase to catch it from any descendant <details>.
    this.addEventListener('toggle', this.#onSubmenuToggle, true);
    window.addEventListener('hashchange', this.#onHashChange);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onLinkClick);
    this.removeEventListener('toggle', this.#onSubmenuToggle, true);
    window.removeEventListener('hashchange', this.#onHashChange);
  }

  /**
   * On mobile the top-level drawer menus are inline <details> accordions. Keep them mutually
   * exclusive: expanding one collapses any other open top-level menu.
   * @param {Event} event
   */
  #onSubmenuToggle = (event) => {
    const opened = event.target;
    if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const topLevelMenus = this.#topLevelMenuDetails();
    if (!topLevelMenus.includes(opened)) return;

    for (const other of topLevelMenus) {
      if (other !== opened && other.open) other.open = false;
    }
  };

  /**
   * The outermost <details> for each top-level drawer menu (wrapped in <accordion-custom> for
   * shallow menus, or a bare .menu-drawer__menu-container for 3-level menus).
   * @returns {HTMLDetailsElement[]}
   */
  #topLevelMenuDetails() {
    const topMenu = this.querySelector('.menu-drawer__menu.has-submenu');
    if (!topMenu) return [];

    return /** @type {HTMLDetailsElement[]} */ (
      Array.from(topMenu.children)
        .map((item) => item.querySelector(':scope > details, :scope > accordion-custom > details'))
        .filter((details) => details instanceof HTMLDetailsElement)
    );
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Close the drawer when a navigation link inside it is tapped.
   *
   * The drawer is a <details scroll-lock>; dialog.js toggles html[scroll-lock]
   * (overflow:hidden) off the details' `open` state. A same-page anchor link
   * (e.g. #cl-card-…) doesn't reload the page, so without an explicit close the
   * <details> stays open and scroll-lock is never released — the page becomes
   * unscrollable. Submenu toggles use <summary> (not a[href]), so they're
   * unaffected here.
   * @param {MouseEvent} event
   */
  #onLinkClick = (event) => {
    if (!this.isOpen || !(event.target instanceof Element)) return;

    const link = event.target.closest('a[href]');
    if (!link || !this.contains(link)) return;

    this.close();
  };

  /**
   * Safety net: any in-page hash navigation (link tap, back/forward) must
   * release the drawer so scroll-lock can't get stranded.
   */
  #onHashChange = () => {
    if (this.isOpen) this.close();
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    // On mobile (≤749px) a submenu is rendered as an inline accordion (a native
    // <details> that expands in place). The slide-in panel behavior — adding
    // `menu-drawer--has-submenu-opened` and, crucially, trapping focus to the
    // opened sub-<details> — must NOT run there: the focus trap redirects focus
    // back into the just-opened submenu, which blocks opening sibling accordions
    // (e.g. Research/Checklist after Shop). Let the native <details> toggle
    // handle expansion and bail out of the JS slide-in logic.
    if (target && window.matchMedia('(max-width: 749px)').matches) return;

    this.#stateVersion += 1;

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const stateVersionAtClose = ++this.#stateVersion;
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (stateVersionAtClose !== this.#stateVersion) return;

        // If the menu has been reopened while the close animation was finishing,
        // ignore this stale close callback and keep the current open state intact.
        if (details.classList.contains('menu-open')) return;

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
